import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarPlus } from "@fortawesome/free-regular-svg-icons";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import type { CreateWaveDatesConfig } from "@/types/waves.types";
import DateAccordion from "@/components/common/DateAccordion";
import DecisionsFirst from "./DecisionsFirst";
import SubsequentDecisions from "./SubsequentDecisions";
import { calculateDecisionTimes, calculateEndDateForCycles } from "../services/waveDecisionService";
import TooltipIconButton from "@/components/common/TooltipIconButton";
import CommonSwitch from "@/components/utils/switch/CommonSwitch";
interface DecisionsProps {
  readonly dates: CreateWaveDatesConfig;
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
  readonly isRollingMode: boolean;
  readonly setIsRollingMode: (isRolling: boolean) => void;
  readonly isExpanded: boolean;
  readonly setIsExpanded: (expanded: boolean) => void;
  readonly onInteraction: () => void;
}
export default function Decisions(t0) {
  const $ = _c(52);
  const {
    dates,
    setDates,
    isRollingMode,
    setIsRollingMode,
    isExpanded,
    setIsExpanded,
    onInteraction
  } = t0;
  const totalDecisionPoints = 1 + dates.subsequentDecisions.length;
  let t1;
  if ($[0] !== dates || $[1] !== isRollingMode || $[2] !== setDates) {
    t1 = decisions => {
      setDates({
        ...dates,
        subsequentDecisions: decisions
      });
      if (!isRollingMode && !dates.isRolling) {
        if (decisions.length === 0) {
          setDates({
            ...dates,
            subsequentDecisions: decisions,
            endDate: dates.firstDecisionTime
          });
        } else {
          const decisionTimes = calculateDecisionTimes(dates.firstDecisionTime, decisions);
          const lastDecisionTime = decisionTimes[decisionTimes.length - 1];
          setDates({
            ...dates,
            subsequentDecisions: decisions,
            endDate: lastDecisionTime ?? null
          });
        }
      }
    };
    $[0] = dates;
    $[1] = isRollingMode;
    $[2] = setDates;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleUpdateSubsequentDecisions = t1;
  let t2;
  if ($[4] !== dates || $[5] !== setDates || $[6] !== setIsRollingMode) {
    t2 = value => {
      if (value && dates.subsequentDecisions.length === 0) {
        alert("You need to add at least one decision interval before enabling recurring mode");
        return;
      }
      setIsRollingMode(value);
      if (value) {
        const twoCompleteRoundsEndDate = calculateEndDateForCycles(dates.firstDecisionTime, dates.subsequentDecisions, 2);
        const minEndDate = dates.subsequentDecisions.length > 0 ? calculateDecisionTimes(dates.firstDecisionTime, dates.subsequentDecisions)[dates.subsequentDecisions.length] : dates.firstDecisionTime;
        const newEndDate = dates.endDate && minEndDate && dates.endDate > minEndDate ? dates.endDate : twoCompleteRoundsEndDate;
        setDates({
          ...dates,
          isRolling: true,
          endDate: newEndDate
        });
      } else {
        let newEndDate_0 = dates.firstDecisionTime;
        if (dates.subsequentDecisions.length > 0) {
          const decisionTimes_0 = calculateDecisionTimes(dates.firstDecisionTime, dates.subsequentDecisions);
          const potentialNewEndDate = decisionTimes_0[decisionTimes_0.length - 1];
          if (potentialNewEndDate) {
            newEndDate_0 = potentialNewEndDate;
          }
        }
        setDates({
          ...dates,
          isRolling: false,
          endDate: newEndDate_0
        });
      }
    };
    $[4] = dates;
    $[5] = setDates;
    $[6] = setIsRollingMode;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleToggleSwitch = t2;
  let t3;
  if ($[8] !== isRollingMode || $[9] !== totalDecisionPoints) {
    t3 = () => <div className="tw-flex tw-items-center tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-rounded-lg tw-shadow-md hover:tw-translate-y-[-1px] tw-transition-transform tw-duration-200"><FontAwesomeIcon icon={faCalendarPlus} className="tw-mr-2 tw-size-4 tw-text-primary-400" /><div className="tw-flex tw-flex-col"><p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Winners</p><div className="tw-flex tw-items-center"><span className="tw-text-sm tw-font-medium tw-text-iron-50">{totalDecisionPoints} announcement{totalDecisionPoints !== 1 ? "s" : ""}</span>{isRollingMode && <span className="tw-text-xs tw-ml-2 tw-px-1.5 tw-rounded tw-bg-blue-500/20 tw-text-blue-400">Recurring</span>}</div></div></div>;
    $[8] = isRollingMode;
    $[9] = totalDecisionPoints;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const renderCollapsedContent = t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="tw-flex tw-items-center tw-gap-x-2"><span>Winners Announcements</span><TooltipIconButton icon={faInfoCircle} tooltipText="Schedule when winners will be announced during your wave. With recurring cycles, announcements repeat until your end date." tooltipPosition="bottom" tooltipWidth="tw-w-80" /></div>;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== isExpanded || $[13] !== setIsExpanded) {
    t5 = () => setIsExpanded(!isExpanded);
    $[12] = isExpanded;
    $[13] = setIsExpanded;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== renderCollapsedContent) {
    t6 = renderCollapsedContent();
    $[15] = renderCollapsedContent;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <strong>Winner announcements</strong>;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== dates.subsequentDecisions.length) {
    t8 = dates.subsequentDecisions.length === 0 && <span className="tw-text-primary-300">{" "}With a fixed schedule, the last announcement marks your wave's end date.</span>;
    $[18] = dates.subsequentDecisions.length;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <p className="tw-mb-0 tw-text-sm tw-text-iron-300">{t7} for showcasing selected creators. Set your first date, then add more if needed.{t8}</p>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-iron-400 tw-italic">Examples: Weekly, monthly, or quarterly announcements.</p>;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t9) {
    t11 = <div className="tw-px-5 tw-pt-2 tw-pb-1"><div className="tw-border-b tw-border-iron-700/50 tw-pb-3 tw-mb-3">{t9}{t10}</div></div>;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== dates || $[26] !== setDates) {
    t12 = time => setDates({
      ...dates,
      firstDecisionTime: time
    });
    $[25] = dates;
    $[26] = setDates;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== dates.firstDecisionTime || $[29] !== dates.votingStartDate || $[30] !== t12) {
    t13 = <DecisionsFirst firstDecisionTime={dates.firstDecisionTime} setFirstDecisionTime={t12} minTimestamp={dates.votingStartDate} />;
    $[28] = dates.firstDecisionTime;
    $[29] = dates.votingStartDate;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== dates.firstDecisionTime || $[33] !== dates.subsequentDecisions || $[34] !== handleUpdateSubsequentDecisions) {
    t14 = <div className="tw-col-span-2"><SubsequentDecisions firstDecisionTime={dates.firstDecisionTime} subsequentDecisions={dates.subsequentDecisions} setSubsequentDecisions={handleUpdateSubsequentDecisions} /></div>;
    $[32] = dates.firstDecisionTime;
    $[33] = dates.subsequentDecisions;
    $[34] = handleUpdateSubsequentDecisions;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== dates.isRolling || $[37] !== dates.subsequentDecisions.length || $[38] !== handleToggleSwitch || $[39] !== isRollingMode) {
    t15 = dates.subsequentDecisions.length > 0 && <div className="tw-col-span-2"><div className="tw-border tw-border-iron-700/50 tw-rounded-lg tw-mt-5 tw-mb-2 tw-shadow-md"><div className="tw-flex tw-justify-between tw-items-center"><div className="tw-flex-1"><h3 className="tw-text-iron-50 tw-text-base tw-font-semibold tw-mb-1">Repeating Announcement Cycles</h3><p className="tw-mb-0 tw-text-xs tw-text-iron-400">Repeat this pattern until your wave ends</p></div><div><CommonSwitch label="Enable recurring cycles" isOn={dates.isRolling ?? isRollingMode} setIsOn={handleToggleSwitch} /></div></div>{(dates.isRolling ?? isRollingMode) && <div className="tw-bg-primary-500/20 tw-border tw-border-primary-500/30 tw-rounded-lg tw-p-3 tw-mt-3 tw-shadow-inner"><p className="tw-mb-0 tw-text-xs tw-text-primary-100"><strong>Recurring cycles enabled.</strong> Announcements will repeat until your wave's end date.</p></div>}</div></div>;
    $[36] = dates.isRolling;
    $[37] = dates.subsequentDecisions.length;
    $[38] = handleToggleSwitch;
    $[39] = isRollingMode;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== onInteraction || $[42] !== t13 || $[43] !== t14 || $[44] !== t15) {
    t16 = <div className="tw-grid tw-grid-cols-1 tw-gap-y-6 tw-gap-x-10 md:tw-grid-cols-2 tw-px-5 tw-pb-5 tw-divide-y tw-divide-solid tw-divide-x-0 tw-divide-iron-700" onClick={onInteraction}>{t13}{t14}{t15}</div>;
    $[41] = onInteraction;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== isExpanded || $[47] !== t11 || $[48] !== t16 || $[49] !== t5 || $[50] !== t6) {
    t17 = <DateAccordion title={t4} isExpanded={isExpanded} onToggle={t5} collapsedContent={t6}>{t11}{t16}</DateAccordion>;
    $[46] = isExpanded;
    $[47] = t11;
    $[48] = t16;
    $[49] = t5;
    $[50] = t6;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  return t17;
}